/**
 * How the maps are called on screen, and in what order.
 *
 * The backend names (`MilitaryBase_v3`, `TheCage`) are what the game binary
 * and the deploy workflow use, and they stay exactly as they are. These are
 * the names people say. Blank_Scene is a developer scene, not a map anyone
 * deploys for players, so the picker leaves it out; a server already on it
 * still shows it by its backend name.
 */
const LABEL: Record<string, string> = {
    MilitaryBase_v3: 'Military Base',
    MilitaryBase_v2: 'Military Base (old)',
    AbandonedRadioStation: 'Radio Station',
    TheHangar: 'The Hangar',
    TheCage: 'The Cage',
}

const ORDER = ['MilitaryBase_v3', 'AbandonedRadioStation', 'TheHangar', 'TheCage']

const HIDDEN = new Set(['Blank_Scene'])

export const mapLabel = (name: string | null | undefined): string => (name ? (LABEL[name] ?? name) : '-')

/** The maps an operator may pick from, in the order they are said. */
export const pickableMaps = <T extends { name: string }>(maps: T[]): T[] =>
    maps
        .filter((m) => !HIDDEN.has(m.name))
        .sort((a, b) => {
            const ia = ORDER.indexOf(a.name)
            const ib = ORDER.indexOf(b.name)
            return (ia === -1 ? ORDER.length : ia) - (ib === -1 ? ORDER.length : ib) || a.name.localeCompare(b.name)
        })

/** How many players a map holds. Set by the map, not typed per server: the
 *  Cage is a 2-player arena whatever anybody types into a box. A map not
 *  listed keeps whatever the form says. */
const MAX_PLAYERS: Record<string, number> = {
    MilitaryBase_v3: 15,
    MilitaryBase_v2: 15,
    AbandonedRadioStation: 4,
    TheHangar: 3,
    TheCage: 2,
}

export const maxPlayersFor = (name: string | null | undefined): number | null =>
    name && name in MAX_PLAYERS ? MAX_PLAYERS[name] : null

/** Where a server actually is, from its slot. `server.region` is what the
 *  game client is told, and the client only knows three regions -- South
 *  America reports as "US". The slot range keeps the four apart. */
const ZONE_BY_SLOT: Record<string, string> = { us: 'NA', sa: 'SA', eu: 'EU', sea: 'SEA' }
const ZONE_LABEL: Record<string, string> = {
    NA: 'North America',
    SA: 'South America',
    EU: 'Europe',
    SEA: 'South-east Asia',
}

export const zoneOfSlot = (slot: string | null | undefined): string | null => {
    const prefix = String(slot ?? '')
        .split('-')[0]
        .toLowerCase()
    return ZONE_BY_SLOT[prefix] ?? null
}

export const zoneLabel = (zone: string | null): string | null => (zone ? (ZONE_LABEL[zone] ?? zone) : null)
